import { useEffect, useState } from "react";

/**
 * Privacy Policy page.
 *
 * Integration notes:
 * 1. Drop this file into your pages directory (e.g. src/pages/PrivacyPolicy.jsx)
 *    and add a route in App.js:
 *      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
 * 2. This component sets document.title and meta tags directly via useEffect
 *    to match your "Page Title | AbhinavJoshi" convention, since I don't have
 *    visibility into whether you're using react-helmet-async or a custom head
 *    hook elsewhere in the codebase. If you already have a <SEO /> or
 *    <PageMeta /> component used on other pages, swap the useEffect block
 *    below for that component instead, to stay consistent.
 * 3. Update og-meta.json / the generate-og-meta.mjs script if this route
 *    should also get server-side OG tags via og.php.
 */

const SECTIONS = [
  { id: "who-this-covers", title: "1. Who This Policy Covers" },
  { id: "information-collected", title: "2. Information I Collect" },
  { id: "how-used", title: "3. How Information Is Used" },
  { id: "legal-basis", title: "4. Legal Basis for Processing (EU/UK/EEA)" },
  { id: "how-processed", title: "5. How Information Is Processed" },
  { id: "cookie-consent", title: "6. Cookie Consent" },
  { id: "retention", title: "7. Data Retention" },
  { id: "security", title: "8. Data Security" },
  { id: "your-rights", title: "9. Your Rights by Region" },
  { id: "children", title: "10. Children's Privacy" },
  { id: "changes", title: "11. Changes to This Policy" },
  { id: "contact", title: "12. Contact and Grievance Officer" },
];

export default function PrivacyPolicy() {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);

  useEffect(() => {
    document.title = "Privacy Policy | AbhinavJoshi";

    const setMeta = (name, content, attr = "name") => {
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    const description =
      "How Abhinav Joshi collects, uses, and protects personal data on abhinavjoshi.com and its marketing tools, covering US, Canada, Australia, New Zealand, Europe, UK, and India.";

    setMeta("description", description);
    setMeta("og:title", "Privacy Policy | AbhinavJoshi", "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", "website", "property");
    setMeta("twitter:card", "summary");
    setMeta("twitter:title", "Privacy Policy | AbhinavJoshi");
    setMeta("twitter:description", description);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: "-40% 0px -50% 0px" }
    );

    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <main className="bg-white text-[#0f1720]">
      <div className="mx-auto max-w-6xl px-6 py-16 md:py-24">
        <header className="max-w-2xl">
          <span className="font-mono text-xs tracking-wide text-[#000d14]/60">
            Last updated September 3, 2026
          </span>
          <h1 className="mt-3 text-4xl font-semibold leading-tight text-[#000d14] md:text-5xl">
            Privacy Policy
          </h1>
          <p className="mt-5 text-base leading-relaxed text-[#0f1720]/70 md:text-lg">
            This page explains what information abhinavjoshi.com and its
            marketing tools collect, how that information is used, and the
            rights available to you, wherever you're visiting from.
          </p>
        </header>

        <div className="mt-14 grid grid-cols-1 gap-12 md:grid-cols-[220px_1fr]">
          {/* Table of contents */}
          <nav
            aria-label="Table of contents"
            className="hidden md:sticky md:top-24 md:block md:h-fit"
          >
            <ul className="space-y-3 border-l border-[#000d14]/10 pl-4">
              {SECTIONS.map((section) => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    className={`block text-sm leading-snug transition-colors ${
                      activeId === section.id
                        ? "font-medium text-[#000d14]"
                        : "text-[#0f1720]/50 hover:text-[#0f1720]/80"
                    }`}
                  >
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Content */}
          <article className="max-w-2xl space-y-14 text-[15px] leading-relaxed text-[#0f1720]/85">
            <p>
              Abhinav Joshi ("I," "me," or "my") operates abhinavjoshi.com
              (the "Site"), including its standalone tools: the Campaign
              Discovery &amp; Execution Plan Engine, the Google Ads Campaign
              Optimizer, and the SEO Audit Tool (collectively, the "Tools").
              By using the Site or the Tools, you agree to the collection and
              use of information as described below. If you do not agree,
              please do not use the Site or the Tools.
            </p>

            <Section id="who-this-covers" title="1. Who This Policy Covers">
              <p>
                This policy applies to visitors of abhinavjoshi.com
                regardless of location. Certain sections apply specific
                rights to residents of particular regions, noted in Section
                9. Where local law provides greater rights than the general
                terms of this policy, local law governs for that region.
              </p>
            </Section>

            <Section
              id="information-collected"
              title="2. Information I Collect"
            >
              <h3 className="text-base font-semibold text-[#000d14]">
                Information you provide directly
              </h3>
              <ul className="mt-3 list-disc space-y-2 pl-5">
                <li>
                  <strong>Contact form:</strong> name, email address, and
                  message content submitted at abhinavjoshi.com/connect.
                </li>
                <li>
                  <strong>Calendly bookings:</strong> name, email, and
                  scheduling details, collected directly by Calendly when you
                  book a call.
                </li>
                <li>
                  <strong>Campaign Optimizer uploads:</strong> CSV export
                  files containing advertising campaign performance data.
                </li>
                <li>
                  <strong>SEO Audit Tool inputs:</strong> the website URL you
                  submit for analysis.
                </li>
                <li>
                  <strong>Campaign Discovery inputs:</strong> business or
                  campaign details you choose to share to generate a plan.
                </li>
              </ul>

              <h3 className="mt-6 text-base font-semibold text-[#000d14]">
                Information collected automatically
              </h3>
              <p className="mt-3">
                IP address, browser type, device type, general location
                (derived from IP), pages visited, and time spent on the Site
                may be collected to understand usage and improve
                performance.
              </p>

              <h3 className="mt-6 text-base font-semibold text-[#000d14]">
                Cookies and similar technologies
              </h3>
              <p className="mt-3">
                The Site may use cookies for essential functionality and,
                where enabled, analytics. Where required by law, non-essential
                cookies are only set after you provide consent through the
                Site's cookie banner. See Section 6 for details.
              </p>
            </Section>

            <Section id="how-used" title="3. How Information Is Used">
              <ul className="list-disc space-y-2 pl-5">
                <li>Responding to inquiries submitted through the contact form</li>
                <li>Scheduling and managing consultation calls booked via Calendly</li>
                <li>Generating outputs from the Tools (campaign plans, optimization reports, SEO audits)</li>
                <li>Improving the Site, the Tools, and the services offered</li>
                <li>Maintaining the security and proper functioning of the Site</li>
                <li>Complying with legal obligations</li>
              </ul>
            </Section>

            <Section
              id="legal-basis"
              title="4. Legal Basis for Processing (EU/UK/EEA Users)"
            >
              <p>
                Where the GDPR or UK GDPR applies, personal data is
                processed on the following bases:
              </p>
              <div className="mt-4 overflow-hidden rounded-xl border border-[#000d14]/10">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-[#000d14]/[0.03] text-left">
                      <th className="px-4 py-3 font-medium text-[#000d14]">Purpose</th>
                      <th className="px-4 py-3 font-medium text-[#000d14]">Legal basis</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#000d14]/10">
                    {[
                      ["Responding to contact form inquiries", "Consent / steps prior to entering a contract"],
                      ["Scheduling calls via Calendly", "Consent / contract"],
                      ["Generating Tool outputs", "Contract / consent"],
                      ["Analytics cookies", "Consent"],
                      ["Essential/functional cookies", "Legitimate interest"],
                      ["Legal or regulatory compliance", "Legal obligation"],
                    ].map(([purpose, basis]) => (
                      <tr key={purpose}>
                        <td className="px-4 py-3 align-top">{purpose}</td>
                        <td className="px-4 py-3 align-top text-[#0f1720]/70">{basis}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4">
                You may withdraw consent at any time by contacting{" "}
                <a href="mailto:mail@abhinavjoshi.com" className="underline underline-offset-2 hover:text-[#000d14]">
                  mail@abhinavjoshi.com
                </a>{" "}
                or adjusting your cookie preferences, without affecting the
                lawfulness of processing carried out before withdrawal.
              </p>
            </Section>

            <Section id="how-processed" title="5. How Information Is Processed">
              <h3 className="text-base font-semibold text-[#000d14]">
                AI processing
              </h3>
              <p className="mt-3">
                The Tools use Google's Gemini API, accessed through a
                server-side proxy, to generate reports and recommendations.
                Data you submit (CSV files, URLs, or campaign details) is
                sent to this API for processing and is not used to train any
                AI model beyond generating your requested output.
              </p>

              <h3 className="mt-6 text-base font-semibold text-[#000d14]">
                Third-party services and sub-processors
              </h3>
              <div className="mt-4 overflow-hidden rounded-xl border border-[#000d14]/10">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-[#000d14]/[0.03] text-left">
                      <th className="px-4 py-3 font-medium text-[#000d14]">Service</th>
                      <th className="px-4 py-3 font-medium text-[#000d14]">Purpose</th>
                      <th className="px-4 py-3 font-medium text-[#000d14]">Location</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#000d14]/10">
                    {[
                      ["Google Gemini API", "AI-generated reports and recommendations", "United States"],
                      ["Google PageSpeed Insights API", "Website performance data for the SEO Audit Tool", "United States"],
                      ["QuickChart.io", "Chart rendering in the Campaign Optimizer", "United States"],
                      ["Thum.io", "Website screenshots in the SEO Audit Tool", "United States"],
                      ["Calendly", "Meeting scheduling", "United States"],
                      ["Hostinger (SMTP)", "Delivery of contact form emails", "EU / Global"],
                    ].map(([service, purpose, location]) => (
                      <tr key={service}>
                        <td className="px-4 py-3 align-top font-medium text-[#0f1720]">{service}</td>
                        <td className="px-4 py-3 align-top text-[#0f1720]/70">{purpose}</td>
                        <td className="px-4 py-3 align-top text-[#0f1720]/70">{location}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4">
                Personal information is not sold or shared for
                cross-context behavioral advertising.
              </p>

              <h3 className="mt-6 text-base font-semibold text-[#000d14]">
                International data transfers
              </h3>
              <p className="mt-3">
                Where personal data is transferred from the EEA, UK,
                Switzerland, or other regions with data transfer
                restrictions to the United States or other countries
                (including through the third-party services listed above),
                such transfers rely on appropriate safeguards, including
                Standard Contractual Clauses (SCCs) or equivalent mechanisms
                offered by those providers.
              </p>
            </Section>

            <Section id="cookie-consent" title="6. Cookie Consent">
              <p>
                Where required by applicable law (including the EU
                ePrivacy Directive, UK PECR, and similar frameworks), the
                Site requests your consent before setting non-essential
                cookies, through a consent banner offering accept, reject,
                and customize options. You can change your preferences at
                any time via the cookie settings link in the Site footer, or
                by clearing cookies in your browser. Essential cookies
                required for the Site to function are not subject to this
                consent requirement.
              </p>
            </Section>

            <Section id="retention" title="7. Data Retention">
              <ul className="list-disc space-y-2 pl-5">
                <li>
                  Contact form submissions are retained as needed to
                  respond to and follow up on inquiries, and are deleted or
                  anonymized when no longer needed.
                </li>
                <li>
                  Tool inputs (CSV files, URLs, campaign details) are
                  processed to generate your report and are not retained
                  beyond what is required for that processing, unless you
                  explicitly request otherwise.
                </li>
                <li>
                  Records may be retained longer where required to comply
                  with legal, accounting, or reporting obligations.
                </li>
              </ul>
            </Section>

            <Section id="security" title="8. Data Security">
              <p>
                Reasonable technical and organizational measures are in
                place to protect the information collected, including
                restricted access to configuration files and credentials,
                and secure handling of API keys through server-side proxies
                rather than exposing them client-side. No method of
                transmission over the internet is completely secure, and
                absolute security cannot be guaranteed.
              </p>
            </Section>

            <Section id="your-rights" title="9. Your Rights by Region">
              <h3 className="text-base font-semibold text-[#000d14]">
                European Union and United Kingdom (GDPR / UK GDPR)
              </h3>
              <p className="mt-3">
                You have the right to access your personal data, request
                correction or erasure, restrict or object to processing,
                request data portability, withdraw consent at any time, and
                lodge a complaint with your local supervisory authority (in
                the UK, the Information Commissioner's Office).
              </p>

              <h3 className="mt-6 text-base font-semibold text-[#000d14]">
                United States
              </h3>
              <p className="mt-3">
                Depending on your state of residence, you may have rights
                under state privacy laws (such as the CCPA/CPRA in
                California and comparable laws in other states) including
                the right to know what personal information is collected,
                request deletion, correct inaccurate information, and opt
                out of the sale or sharing of personal information, or of
                targeted advertising. Personal information is not sold or
                used for cross-context behavioral advertising.
              </p>

              <h3 className="mt-6 text-base font-semibold text-[#000d14]">
                Canada
              </h3>
              <p className="mt-3">
                Personal information is handled in accordance with the
                Personal Information Protection and Electronic Documents Act
                (PIPEDA) and applicable provincial legislation. You have the
                right to access your personal information, challenge its
                accuracy, and request correction, and may withdraw consent
                for optional uses at any time, subject to legal or
                contractual restrictions.
              </p>

              <h3 className="mt-6 text-base font-semibold text-[#000d14]">
                Australia and New Zealand
              </h3>
              <p className="mt-3">
                If you are located in Australia, personal information is
                handled in accordance with the Australian Privacy
                Principles under the Privacy Act 1988 (Cth). If you are
                located in New Zealand, personal information is handled in
                accordance with the Privacy Act 2020. You have the right to
                access and request correction of your personal information,
                and to make a complaint to the OAIC or New Zealand's Office
                of the Privacy Commissioner, as applicable.
              </p>

              <h3 className="mt-6 text-base font-semibold text-[#000d14]">
                India
              </h3>
              <p className="mt-3">
                Personal data is handled in accordance with the Digital
                Personal Data Protection Act, 2023 (DPDPA), where
                applicable. You have the right to access your personal
                data, request correction or erasure, withdraw consent, and
                nominate another individual to exercise your rights in the
                event of death or incapacity. Grievances can be raised with
                the Grievance Officer identified in Section 12.
              </p>

              <h3 className="mt-6 text-base font-semibold text-[#000d14]">
                Exercising your rights
              </h3>
              <p className="mt-3">
                To exercise any of the rights described above, contact{" "}
                <a href="mailto:mail@abhinavjoshi.com" className="underline underline-offset-2 hover:text-[#000d14]">
                  mail@abhinavjoshi.com
                </a>
                . A response will be provided within the timeframe required
                by applicable law. Identity verification may be requested
                before fulfilling certain requests.
              </p>
            </Section>

            <Section id="children" title="10. Children's Privacy">
              <p>
                The Site and Tools are not directed at children and are not
                intended for use by individuals under 18 years of age (or
                the age of digital consent in your jurisdiction, if lower,
                but in no case under 13). Personal information from
                children is not knowingly collected. If you believe a child
                has provided personal information, contact{" "}
                <a href="mailto:mail@abhinavjoshi.com" className="underline underline-offset-2 hover:text-[#000d14]">
                  mail@abhinavjoshi.com
                </a>{" "}
                so it can be removed.
              </p>
            </Section>

            <Section id="changes" title="11. Changes to This Policy">
              <p>
                This Privacy Policy may be updated from time to time to
                reflect changes in practices or legal requirements. Material
                changes will be posted on this page with an updated "Last
                updated" date. Continued use of the Site after changes are
                posted constitutes acceptance of the revised policy.
              </p>
            </Section>

            <Section
              id="contact"
              title="12. Contact and Grievance Officer"
            >
              <p>
                Questions, requests, or complaints about this Privacy
                Policy or the handling of your personal data can be
                directed to:
              </p>
              <p className="mt-3">
                <strong>Email:</strong>{" "}
                <a href="mailto:mail@abhinavjoshi.com" className="underline underline-offset-2 hover:text-[#000d14]">
                  mail@abhinavjoshi.com
                </a>
                <br />
                <strong>Website:</strong> abhinavjoshi.com
              </p>
              <p className="mt-3 text-[#0f1720]/70">
                For users in India, the above contact also serves as the
                Grievance Officer under the Digital Personal Data Protection
                Act, 2023.
              </p>
            </Section>
          </article>
        </div>
      </div>
    </main>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-xl font-semibold text-[#000d14] md:text-2xl">
        {title}
      </h2>
      <div className="mt-4 space-y-4">{children}</div>
    </section>
  );
}
